import React, { useEffect, useRef, useState } from 'react';
import Avatar from 'react-avatar';
import { useQuery } from '@tanstack/react-query';
import { getPostLikers } from '../../services/api/implementation/impl';

const SHOWN = 20;

// The like count as a button; clicking it lists who liked the post. `label`
// replaces the default "N likes" text.
const LikersPopover = ({ postId, count, label, className = '', buttonClassName = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data, isFetching, isError } = useQuery({
    queryKey: ['postLikers', postId, count],
    queryFn: () => getPostLikers(postId, 1, SHOWN),
    enabled: open && !!postId,
    staleTime: 0
  });

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  if (!count) return null;
  const likers = data?.likers || [];
  const rest = (data?.total ?? count) - likers.length;

  return (
    <span className={`relative ${className}`} ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`See who liked this (${count})`}
        onClick={() => setOpen((value) => !value)}
        className={buttonClassName || 'text-xs text-gray-500 hover:text-brand-700 hover:underline'}>
        {label ?? `${count} ${count === 1 ? 'like' : 'likes'}`}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="People who liked this"
          className="absolute bottom-full left-0 z-30 mb-2 w-60 rounded-panel border border-hairline bg-surface p-2 shadow-lg">
          <p className="px-1.5 pb-1.5 text-xs font-semibold text-gray-700">Liked by</p>
          {isFetching && likers.length === 0 ? (
            <p className="px-1.5 py-2 text-xs text-gray-500">Loading…</p>
          ) : isError ? (
            <p className="px-1.5 py-2 text-xs text-gray-500">Couldn&apos;t load the list.</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {likers.map(({ user }) => (
                <li key={user?.id} className="flex items-center gap-2 rounded-action px-1.5 py-1">
                  <Avatar
                    name={user?.name || ''}
                    src={user?.user_pic?.url}
                    size="24"
                    round
                  />
                  <span className="truncate text-sm text-gray-800">{user?.name || 'Someone'}</span>
                </li>
              ))}
              {rest > 0 && (
                <li className="px-1.5 py-1 text-xs text-gray-500">and {rest} more</li>
              )}
            </ul>
          )}
        </div>
      )}
    </span>
  );
};

export default LikersPopover;
